/** Markdown prefix that should wrap the current paragraph as a list. */
export function markdownPrefixToListKind(text: string): 'ordered' | 'bullet' | null {
  const trimmed = text.replace(/\u00a0/g, ' ');
  if (/^\d+\.$/.test(trimmed) || /^\d+\.\s$/.test(trimmed)) return 'ordered';
  if (/^[-*+]$/.test(trimmed) || /^[-*+]\s$/.test(trimmed)) return 'bullet';
  return null;
}

/** Digits-only paragraph: typing `.` should start an ordered list at that number. */
export function orderedListStartFromDigits(text: string): number | null {
  if (!/^\d+$/.test(text)) return null;
  const start = Number.parseInt(text, 10);
  if (!Number.isFinite(start) || start < 1) return null;
  return start;
}

/**
 * Empty list item + Enter should lift/outdent that line instead of creating
 * another blank item (Word / Google Docs / Slack behavior).
 */
export function shouldLiftEmptyListItem(params: {
  selectionEmpty: boolean;
  inListItem: boolean;
  currentBlockEmpty: boolean;
}): boolean {
  return params.selectionEmpty && params.inListItem && params.currentBlockEmpty;
}

export type PlainListEnterAction = 'none' | 'exit' | 'outdent' | { prefix: string };

const ROMAN_TOKENS = [
  'i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
  'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx',
];

export interface PlainListLine {
  indent: string;
  token: string;
  kind: 'number' | 'alpha' | 'roman' | 'bullet';
  /** True when the marker has no item text (`1. ` / `  a. `). A bare `1.` or `-` is not empty. */
  empty: boolean;
}

export function listIndentDepth(indent: string): number {
  let width = 0;
  for (const ch of indent) width += ch === '\t' ? 2 : 1;
  return Math.floor(width / 2);
}

function isRomanToken(token: string): boolean {
  return ROMAN_TOKENS.includes(token.toLowerCase());
}

function markerKind(token: string, depth: number): PlainListLine['kind'] | null {
  if (/^[-*+]$/.test(token)) return 'bullet';
  if (/^\d+$/.test(token)) return 'number';
  if (isRomanToken(token) && (token.length > 1 || depth >= 2)) return 'roman';
  if (/^[a-zA-Z]$/.test(token)) return 'alpha';
  return null;
}

function nextAlpha(token: string): string {
  const upper = token === token.toUpperCase();
  let n = 0;
  for (const ch of token.toLowerCase()) n = n * 26 + (ch.charCodeAt(0) - 96);
  n += 1;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(97 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return upper ? out.toUpperCase() : out;
}

function nextRoman(token: string): string {
  const upper = token === token.toUpperCase() && token.toLowerCase() !== token;
  const idx = ROMAN_TOKENS.indexOf(token.toLowerCase());
  const next = ROMAN_TOKENS[Math.min(idx + 1, ROMAN_TOKENS.length - 1)] || token.toLowerCase();
  return upper ? next.toUpperCase() : next;
}

export function nextListToken(token: string, kind: PlainListLine['kind']): string {
  if (kind === 'bullet') return token;
  if (kind === 'number') return String(Number.parseInt(token, 10) + 1);
  if (kind === 'roman') return nextRoman(token);
  return nextAlpha(token);
}

function formatMarker(token: string, kind: PlainListLine['kind']): string {
  return kind === 'bullet' ? `${token} ` : `${token}. `;
}

/** First marker used when a line is indented into a new subsection. */
function subsectionToken(kind: PlainListLine['kind'], depth: number): { token: string; kind: PlainListLine['kind'] } {
  if (kind === 'bullet') return { token: '-', kind: 'bullet' };
  if (depth === 1) return { token: 'a', kind: 'alpha' };
  if (depth >= 2) return { token: 'i', kind: 'roman' };
  return { token: '1', kind: 'number' };
}

export function parsePlainListLine(line: string): PlainListLine | null {
  const bullet = line.match(/^(\s*)([-*+])(\s*)(.*)$/);
  if (bullet && (bullet[3].length > 0 || bullet[4].length === 0)) {
    const rest = bullet[4];
    const depth = listIndentDepth(bullet[1]);
    const kind = markerKind(bullet[2], depth);
    if (!kind) return null;
    return {
      indent: bullet[1],
      token: bullet[2],
      kind,
      empty: rest.length === 0 && bullet[3].length > 0,
    };
  }
  const ordered = line.match(/^(\s*)(\d+|[a-zA-Z]+)\.(\s*)(.*)$/);
  if (!ordered) return null;
  const depth = listIndentDepth(ordered[1]);
  const kind = markerKind(ordered[2], depth);
  if (!kind || kind === 'bullet') return null;
  const rest = ordered[4];
  if (rest.length > 0 && ordered[3].length === 0) return null;
  return {
    indent: ordered[1],
    token: ordered[2],
    kind,
    empty: rest.length === 0 && ordered[3].length > 0,
  };
}

function reduceIndent(indent: string): string {
  if (indent.startsWith('\t')) return indent.slice(1);
  if (indent.startsWith('  ')) return indent.slice(2);
  if (indent.startsWith(' ')) return indent.slice(1);
  return '';
}

function previousListAtIndent(lines: string[], beforeIndex: number, indent: string): PlainListLine | null {
  const width = listIndentDepth(indent);
  for (let i = beforeIndex - 1; i >= 0; i--) {
    const parsed = parsePlainListLine(lines[i]);
    if (!parsed || parsed.empty) continue;
    if (listIndentDepth(parsed.indent) === width) return parsed;
  }
  return null;
}

/**
 * Plain-text comment boxes have no TipTap list. Enter on a list line should
 * keep the same indent and marker style. An empty marker leaves the list, or
 * steps back to the parent item when the line is indented.
 */
export function plainListEnterAction(line: string): PlainListEnterAction {
  const parsed = parsePlainListLine(line);
  if (!parsed) return 'none';
  if (parsed.empty) return parsed.indent.length > 0 ? 'outdent' : 'exit';
  return { prefix: `${parsed.indent}${formatMarker(nextListToken(parsed.token, parsed.kind), parsed.kind)}` };
}

function replaceLine(
  value: string,
  lineStart: number,
  lineEnd: number,
  replacement: string,
): { value: string; cursor: number } {
  const next = `${value.slice(0, lineStart)}${replacement}${value.slice(lineEnd)}`;
  return { value: next, cursor: lineStart + replacement.length };
}

/** Insert the next list marker, or leave an empty marker. Null when the line is not a list. */
export function continuePlainCommentList(
  value: string,
  cursor: number,
): { value: string; cursor: number } | null {
  const lineStart = value.lastIndexOf('\n', cursor - 1) + 1;
  const nextBreak = value.indexOf('\n', cursor);
  const lineEnd = nextBreak === -1 ? value.length : nextBreak;
  if (cursor !== lineEnd) return null;
  const line = value.slice(lineStart, lineEnd);
  const action = plainListEnterAction(line);
  if (action === 'none') return null;
  if (action === 'exit') {
    const next = `${value.slice(0, lineStart)}\n${value.slice(lineEnd)}`;
    return { value: next, cursor: lineStart + 1 };
  }
  if (action === 'outdent') {
    const parsed = parsePlainListLine(line);
    if (!parsed) return null;
    const lines = value.split('\n');
    const lineIndex = value.slice(0, lineStart).split('\n').length - 1;
    const parentIndent = reduceIndent(parsed.indent);
    const parent = previousListAtIndent(lines, lineIndex, parentIndent);
    const replacement = parent
      ? `${parentIndent}${formatMarker(nextListToken(parent.token, parent.kind), parent.kind)}`
      : `${parentIndent}${formatMarker('1', 'number')}`;
    return replaceLine(value, lineStart, lineEnd, replacement);
  }
  const insert = `\n${action.prefix}`;
  return {
    value: value.slice(0, cursor) + insert + value.slice(cursor),
    cursor: cursor + insert.length,
  };
}

/**
 * Tab indents the current list line into a subsection (1. → a. → i.).
 * Shift-Tab steps back one level and continues the parent sequence.
 */
export function changePlainListIndent(
  value: string,
  cursor: number,
  direction: 'in' | 'out',
): { value: string; cursor: number } | null {
  const lineStart = value.lastIndexOf('\n', cursor - 1) + 1;
  const nextBreak = value.indexOf('\n', cursor);
  const lineEnd = nextBreak === -1 ? value.length : nextBreak;
  const line = value.slice(lineStart, lineEnd);
  const parsed = parsePlainListLine(line);
  if (!parsed || parsed.empty) return null;
  const rest = line.slice(parsed.indent.length + formatMarker(parsed.token, parsed.kind).length);
  if (direction === 'in') {
    const indent = `${parsed.indent}  `;
    const depth = listIndentDepth(indent);
    const next = parsed.kind === 'bullet'
      ? { token: parsed.token, kind: parsed.kind }
      : subsectionToken(parsed.kind, depth);
    const replacement = `${indent}${formatMarker(next.token, next.kind)}${rest}`;
    return replaceLine(value, lineStart, lineEnd, replacement);
  }
  if (!parsed.indent) return null;
  const indent = reduceIndent(parsed.indent);
  const lines = value.split('\n');
  const lineIndex = value.slice(0, lineStart).split('\n').length - 1;
  const parent = previousListAtIndent(lines, lineIndex, indent);
  let token = parsed.token;
  let kind = parsed.kind;
  if (parsed.kind !== 'bullet') {
    if (parent && parent.kind !== 'bullet') {
      token = nextListToken(parent.token, parent.kind);
      kind = parent.kind;
    } else {
      const stepped = subsectionToken(parsed.kind, listIndentDepth(indent));
      token = stepped.token;
      kind = stepped.kind;
    }
  }
  const replacement = `${indent}${formatMarker(token, kind)}${rest}`;
  return replaceLine(value, lineStart, lineEnd, replacement);
}

type PlainListKeyEvent = {
  key: string;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  preventDefault: () => void;
};

/**
 * `enter` — Enter inserts a newline (Cmd/Ctrl-Enter sends).
 * `shift-enter` — Enter sends; Shift+Enter inserts a newline.
 */
export function applyPlainCommentListKeydown(
  event: PlainListKeyEvent,
  textarea: HTMLTextAreaElement,
  onChange: (value: string) => void,
  mode: 'enter' | 'shift-enter',
): boolean {
  if (event.key === 'Tab' && !event.metaKey && !event.ctrlKey && !event.altKey) {
    if (textarea.selectionStart !== textarea.selectionEnd) return false;
    const indented = changePlainListIndent(
      textarea.value,
      textarea.selectionStart,
      event.shiftKey ? 'out' : 'in',
    );
    if (!indented) return false;
    event.preventDefault();
    onChange(indented.value);
    const cursor = indented.cursor;
    requestAnimationFrame(() => {
      textarea.setSelectionRange(cursor, cursor);
    });
    return true;
  }
  if (event.key !== 'Enter' || event.metaKey || event.ctrlKey || event.altKey) return false;
  if (mode === 'shift-enter' && !event.shiftKey) return false;
  if (textarea.selectionStart !== textarea.selectionEnd) return false;
  const next = continuePlainCommentList(textarea.value, textarea.selectionStart);
  if (!next) return false;
  event.preventDefault();
  onChange(next.value);
  const cursor = next.cursor;
  requestAnimationFrame(() => {
    textarea.setSelectionRange(cursor, cursor);
  });
  return true;
}
